/**
 * Copy for the User Roles guide.
 *
 * The screen used to ship with no explanation at all: a table of every internal
 * user, two columns whose meaning is not self-evident ("Role", "Temporary
 * Support") and two actions that change what other people can see and do. Every
 * sentence below is written from what the screen actually enforces — the
 * permission computeds in `auth.store.js` and the cells in
 * `recruiter-role.store.js` — not from an idealised description of the feature.
 */

/**
 * The four things a user can do on this screen, in the order they do them.
 * Rendered as the numbered list inside the "How this screen works" panel.
 */
export const ROLE_GUIDE_STEPS = [
	{
		step: 1,
		color: '#0F58E4',
		title: 'Find the person',
		description:
			'Type a name or email into the search box. Selecting a suggestion filters the table to that person, or press Enter to search everyone. Clear the box to restore the full list.',
	},
	{
		step: 2,
		color: '#7B61FF',
		title: 'Read their access',
		description:
			'The Role chip shows what that person can do in Maestro. The Sign-in column says whether they can actually get in — signed in, invited and waiting, never invited, or invite expired. Temporary Support lists the colleagues who can also see this person’s candidates. Hover the ⓘ next to any column name for details.',
	},
	{
		step: 3,
		color: '#F5A623',
		title: 'Change role or cover',
		description:
			'Open the Assign role dialog from the person icon in the Action column. Admins can change both the role and the cover list; account managers can change the cover list only. No icon means the row can’t be edited from here.',
	},
	{
		step: 4,
		color: '#37AA00',
		title: 'Check the result',
		description:
			'Click a name to open their detail page. Use View as Recruiter to load Maestro exactly as that recruiter sees it, then leave from the banner at the top to return as yourself.',
	},
]

/**
 * What each role can actually do.
 *
 * `scope` matches `MaestroRoleEnum` as it arrives on `recruiter.roles[].scope`.
 * The list is keyed rather than ordered because a tenant's role picklist comes
 * from master data and may not contain all four.
 */
export const ROLE_DEFINITIONS = {
	maestro_admin: {
		label: 'Admin',
		summary: 'Full control of the portal, including who else can do what.',
		abilities: ['Opens this User Roles screen', 'Changes anyone’s role', 'Assigns temporary support', 'Uses View as Recruiter'],
	},
	maestro_account_manager: {
		label: 'Account Manager',
		summary: 'Runs the client relationship and can cover for recruiters, but cannot hand out permissions.',
		abilities: ['Opens this User Roles screen', 'Assigns temporary support', 'Uses View as Recruiter', 'Cannot change anyone’s role'],
	},
	maestro_manager: {
		label: 'Manager',
		summary: 'Oversees a team’s jobs, candidates and reporting.',
		abilities: ['Works their team’s jobs, candidates and reports', 'Does not open this screen', 'Does not change roles or cover'],
	},
	maestro_recruiter: {
		label: 'Recruiter',
		summary: 'Day-to-day sourcing, submitting and placing. The role most of the list will have.',
		abilities: ['Works their own jobs and candidates', 'Does not open this screen', 'Can be viewed by an admin via View as Recruiter'],
	},
}

/**
 * Fallback for a tenant-specific role this file does not know about, so an
 * unrecognised chip still gets an honest explanation instead of a blank card.
 */
export const UNKNOWN_ROLE_DEFINITION = {
	summary: 'A role configured for your company. It decides which screens and actions this person sees in Maestro.',
	abilities: [],
}

export const getRoleDefinition = (scope) => ROLE_DEFINITIONS[scope] || UNKNOWN_ROLE_DEFINITION

/**
 * The roles whose day-to-day work *is* a candidate desk.
 *
 * A user created in Maestro owns no candidates of their own — `Worker.employerId`
 * is only ever written by ATS sync — so one of these created with an empty cover
 * list opens Maestro to a candidate list with nothing in it. That is worth saying
 * out loud in the Add user dialog.
 *
 * Admin and Account Manager are deliberately absent: they have screens of their
 * own and are routinely created with nobody to cover, which is exactly the case
 * an earlier `min(1)` rule on the cover list made impossible. Anything a tenant
 * adds to its own role picklist is absent too, and gets no warning — better a
 * missing warning than one shown to every credentialing or billing role a company
 * invents.
 *
 * Values are `MaestroRoleEnum`, matching `recruiterRoles[].value` from master data.
 */
export const CANDIDATE_DESK_ROLES = ['maestro_recruiter', 'maestro_manager']

export const worksCandidateDesk = (scope) => CANDIDATE_DESK_ROLES.includes(scope)

/**
 * Column-header help. Keys match the `key` of each column in
 * `recruiterRoleStore.tableHeader`.
 */
export const ROLE_COLUMN_INFO = {
	fullName: {
		title: 'Name',
		description: 'The person’s name and the email they sign in with. Click the name to open their detail page; hover the email to copy it.',
	},
	roles: {
		title: 'Role',
		description: 'The permission level this person holds in Maestro. One role per person — assigning a new one replaces the old one.',
		note: 'Filter the list by role from the icon next to this header.',
	},
	supporters: {
		title: 'Temporary Support',
		description:
			'The colleagues who can also see this person’s candidates. The row is the recruiter being covered and the list is who covers them — not the other way round. It widens what those people see; it does not change anyone’s role.',
		// "Temporary" is a misnomer the backend does not back up: no end date is
		// stored anywhere, so anyone reading the column name for a guarantee gets
		// a standing grant instead.
		note: 'Despite the name there is no end date — access lasts until somebody removes it by hand.',
	},
	action: {
		title: 'Action',
		description:
			'Opens the Assign role dialog for this person. Admins also see a mail icon when someone has not signed in yet — never invited, invite expired, or invited but still waiting — to send or resend their welcome email.',
		note: 'No icon means this user’s access cannot be edited from here.',
	},
	signIn: {
		title: 'Sign-in',
		description:
			'Whether this person has actually signed in to Maestro. Signed-in users are fine. Never invited or invite expired means they cannot get in until an admin sends an invitation from the mail icon in Action. Invited but not yet signed in may still need a resend if the email was lost.',
	},
	viewAsRecruiter: {
		title: 'View as Recruiter',
		description:
			'Loads Maestro as that recruiter sees it — their dashboard, their jobs, their candidates — so you can check what they are looking at. Only shown for people who hold the Recruiter role.',
		note: 'A banner stays at the top of the screen for the whole session. Leave it to return as yourself.',
	},
}

/**
 * Help shown inside the Assign role dialog. The dialog changes what a colleague
 * can see and do, so it says plainly what each field means before the user
 * confirms.
 */
export const ASSIGN_ROLE_DIALOG_HELP = {
	role: 'One role per person. Saving replaces their current role, which changes the screens they can open and the data that rolls up to reports.',
	temporarySupport:
		'Anyone you add here can also see this person’s candidates, on top of their own role. Despite the field name nothing expires — the access lasts until you come back and remove it.',
}

/** Shown under the guide panel’s heading, above the numbered steps. */
export const ROLE_GUIDE_INTRO =
	'This screen lists every internal Maestro user at your company. Use it to check who holds each permission level, arrange cover while someone is away, and open a user’s detail page.'
